// Progressive codec analysis: decision lanes and stored-coefficient columns are
// computed in small idle-time slices so the page stays responsive.
import type { CodecAnalysis } from '../codecs/types';
import type { Model } from '../model';

/** Colors for lane values, ordered: calm → dramatic. */
export const LANE_COLORS = ['#2dd4bf', '#38bdf8', '#a78bfa', '#f472b6', '#f5b841', '#fb923c', '#f87171', '#94a3b8'];

/** Stored spectrum columns, quantized to bytes (dB) to keep memory small. */
export interface StoredColumn { t0: number; t1: number; nyquist: number; v: Uint8Array }

const DB_FLOOR = -100, DB_CEIL = 0;
const MAX_BINS = 512;

export class CodecCache {
  readonly lanes: Int8Array[];
  decided = 0;
  columns: StoredColumn[] = [];
  columnsDone = 0;
  maxNyquist = 0;
  private wantColumns = false;
  private timer = 0;
  private listeners = new Set<() => void>();
  private disposed = false;

  constructor(readonly m: Model, readonly c: CodecAnalysis) {
    this.lanes = c.lanes.map(() => new Int8Array(m.frames.length).fill(-1));
    this.schedule();
  }

  on(f: () => void) {
    this.listeners.add(f);
  }
  private emit() {
    this.listeners.forEach((f) => f());
  }

  get decisionsDone() {
    return this.decided >= this.m.frames.length;
  }
  get hasColumns() {
    return !!this.c.columns;
  }
  /** Starts decoding stored columns (only when the user first asks for that view). */
  requestColumns() {
    if (!this.c.columns || this.wantColumns) return;
    this.wantColumns = true;
    this.schedule();
  }

  private schedule() {
    if (this.timer || this.disposed) return;
    this.timer = window.setTimeout(() => {
      this.timer = 0;
      this.work();
    }, 0);
  }

  private work() {
    const t0 = performance.now();
    const budget = 12;
    const F = this.m.frames.length;
    let changed = false;
    while (performance.now() - t0 < budget) {
      if (this.decided < F) {
        const i = this.decided++;
        try {
          const d = this.c.decisions(i);
          for (let k = 0; k < this.lanes.length; k++) this.lanes[k][i] = d[k] ?? -1;
        } catch { /* leave as unknown */ }
        changed = true;
      } else if (this.wantColumns && this.columnsDone < F) {
        const i = this.columnsDone++;
        try {
          for (const col of this.c.columns!(i)) this.columns.push(quantize(col.t0, col.t1, col.nyquist, col.mags));
        } catch { /* damaged frame: leave a gap */ }
        changed = true;
      } else break;
    }
    if (changed) {
      if (this.columns.length) this.maxNyquist = Math.max(this.maxNyquist, this.columns[this.columns.length - 1].nyquist);
      this.emit();
    }
    if (this.decided < F || (this.wantColumns && this.columnsDone < F)) this.schedule();
  }

  /** Index of the stored column covering time t (columns are appended in time order). */
  columnAt(t: number): number {
    const a = this.columns;
    let lo = 0, hi = a.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (a[mid].t0 <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return ans >= 0 && t < a[ans].t1 + 1e-9 ? ans : -1;
  }

  dispose() {
    this.disposed = true;
    clearTimeout(this.timer);
    this.listeners.clear();
  }
}

function quantize(t0: number, t1: number, nyquist: number, mags: Float32Array): StoredColumn {
  const n = Math.min(MAX_BINS, mags.length);
  const v = new Uint8Array(n);
  const step = mags.length / n;
  for (let k = 0; k < n; k++) {
    let mx = 0;
    const a = Math.floor(k * step), b = Math.max(a + 1, Math.floor((k + 1) * step));
    for (let j = a; j < b; j++) { const x = Math.abs(mags[j]); if (x > mx) mx = x; }
    if (mx <= 0) { v[k] = 0; continue; }
    const db = 20 * Math.log10(mx);
    v[k] = Math.max(1, Math.min(255, Math.round(((db - DB_FLOOR) / (DB_CEIL - DB_FLOOR)) * 254) + 1));
  }
  return { t0, t1, nyquist, v };
}
